import React from 'react';
import { CircleCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProductType } from '../../../../../common/models/product-type.model';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';
import { resolveTranslation } from '../../../../../common/utils/translatable-string';

// =============================================================================
// SVG Product Type Icons
// =============================================================================

/** Hardwood — rounded square with wood grain lines */
const HardwoodSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 2v28" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M20 2v28" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M7 8c2-1 4 2 2 5s-3 4-1 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
    <path d="M15 6c-1.5 2-2 5 0 8s1 6-1 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
    <path d="M24 5c-1 3 1 5 0 9s1 5 0 8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
  </svg>
);

/** Tile — grid of square tiles */
const TileSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="18" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="5" y="18" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="18" y="18" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
  </svg>
);

/** Vinyl — horizontal planks with subtle texture */
const VinylSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 10h22M5 16h22M5 22h22" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M16 4v6M12 10v6M20 16v6M14 22v6" stroke="currentColor" strokeWidth="1" strokeOpacity="0.35"/>
  </svg>
);

/** Laminate — layered planks */
const LaminateSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 9h22M5 16h22M5 23h22" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M18 4v5M11 9v7M22 16v7M15 23v5" stroke="currentColor" strokeWidth="1" strokeOpacity="0.4"/>
    <path d="M8 5c1 1 0 3 1 4" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeOpacity="0.35"/>
    <path d="M25 11c-1 1.5 0 3-1 4" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeOpacity="0.35"/>
  </svg>
);

/** Carpeting — crosshatch weave pattern */
const CarpetingSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 6v20M12 6v20M16 6v20M20 6v20M24 6v20" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3"/>
    <path d="M6 8h20M6 12h20M6 16h20M6 20h20M6 24h20" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3"/>
    <circle cx="10" cy="10" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="16" cy="16" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="22" cy="22" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="22" cy="10" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="10" cy="22" r="1" fill="currentColor" fillOpacity="0.25"/>
  </svg>
);

/** Stones — irregular stone shapes */
const StonesSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 13c2-3 6-4 9-2s5 1 8-1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M6 21c3-2 5 0 8-1s6-3 9-1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M13 5c-1 3-1 6 0 9" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
    <path d="M21 14c1 2 0 5-1 7" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
    <path d="M9 20c0 2 1 4 2 6" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
  </svg>
);

/** Wallpaper — a strip of paper hung from its roll, a motif repeated down it */
const WallpaperSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 7h11v19H10z" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6" strokeLinejoin="round"/>
    <path d="M21 7c2 0 3 .9 3 2.2S23 11.5 21 11.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M15.5 10l2 2.5-2 2.5-2-2.5zM15.5 18l2 2.5-2 2.5-2-2.5z" stroke="currentColor" strokeWidth="0.8" strokeLinejoin="round" strokeOpacity="0.45"/>
  </svg>
);

/** Generic fallback */
const GenericTypeSvg: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <circle cx="16" cy="16" r="2" fill="currentColor" fillOpacity="0.3"/>
  </svg>
);

const PRODUCT_TYPE_SVG_MAP: Record<string, React.FC> = {
  hardwood: HardwoodSvg,
  tile: TileSvg,
  vinyl: VinylSvg,
  laminate: LaminateSvg,
  carpeting: CarpetingSvg,
  carpet: CarpetingSvg,
  stones: StonesSvg,
  stone: StonesSvg,
  wallpaper: WallpaperSvg,
};

interface ProductTypeStepProps {
  productTypes: ProductType[];
  selectedType: string | null;
  onSelect: (typeName: string) => void;
}

export const ProductTypeStep: React.FC<ProductTypeStepProps> = ({
  productTypes,
  selectedType,
  onSelect,
}) => {
  const { rt } = useResolveTranslation();

  const getSvgIcon = (slug: string): React.FC => {
    return PRODUCT_TYPE_SVG_MAP[slug.toLowerCase()] || GenericTypeSvg;
  };

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {productTypes.map((pt) => {
        const ptDisplayName = rt(pt.name);
        const ptEnglishName = resolveTranslation(pt.name, 'en');
        const isSelected = selectedType?.toLowerCase() === ptEnglishName.toLowerCase();
        const IconComponent = getSvgIcon(pt.slug);

        return (
          <div
            key={pt._id}
            data-testid={`product-type-card-${pt.slug}`}
            onClick={() => onSelect(ptEnglishName)}
            className={cn(
              'relative flex h-[120px] cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden border-[1.5px] bg-card transition-colors',
              isSelected ? 'border-foreground' : 'border-border hover:border-foreground/40'
            )}
          >
            {isSelected && (
              <CircleCheck className="absolute top-2.5 end-2.5 size-[18px] text-foreground" />
            )}

            <span className={isSelected ? 'text-foreground' : 'text-muted-foreground'}>
              <IconComponent />
            </span>

            <p className="text-sm font-medium capitalize text-foreground">{ptDisplayName}</p>
          </div>
        );
      })}
    </div>
  );
};
